import React, {
    useState,
    useMemo,
    useEffect,
    useCallback,
    useRef
} from 'react';
import { useGlobalDataContext } from '@context/GlobalData/GlobalDataContext';
import { first, last } from 'lodash';
import { formatPriceValue, formatMileageValue } from '@utils';
import Select from 'react-select';
import { searchStates } from '@config';

const Range = ({ title, options }) => {
    const { globalContext, setGlobalContext } = useGlobalDataContext();

    /**
     * We use this useRef to prevent the useEffect from running on the first render.
     * This is currently a patch for something can could be done better in the future.
     *
     * FIXME: Make it so the component won't need this.
     */
    const didMount = useRef(false);

    const minStartValue = useMemo(() => first(options), [options]);
    const maxStartValue = useMemo(() => last(options), [options]);

    /**
     * Get min and max value from the globalContext.
     * If the range is not set both values will be undefined.
     *
     * @returns {[(string|number), (string|number)]}
     */
    const [minValueContextData, maxValueContextData] = useMemo(
        () => globalContext.facets[title]?.range ?? [],
        [globalContext, title]
    );

    /**
     * Get the options that have the same value as the selected facets.
     * With these values we can decide to use the pre-selected data or
     * get the min and max start values.
     */
    const [selectedMinValue, selectedMaxValue] = useMemo(
        () => [
            options.find(({ value }) => value === minValueContextData),
            options.find(({ value }) => value === maxValueContextData)
        ],
        [options, minValueContextData, maxValueContextData]
    );

    /**
     * Get either the pre-selected min value or the start value.
     */
    const [minValue, setMinValue] = useState(
        () => selectedMinValue ?? minStartValue
    );

    /**
     * Get either the pre-selected max value or the start value.
     */
    const [maxValue, setMaxValue] = useState(
        () => selectedMaxValue ?? maxStartValue
    );

    /**
     * Calculate the options for the min range.
     * These options are all below the value of the maxValue state.
     *
     * @returns {object[]}
     */
    const minRange = useMemo(
        () =>
            options.filter(
                ({ value }) => Number(value) < Number(maxValue.value)
            ),
        [options, maxValue]
    );

    /**
     * Calculate the options for the max range.
     * These options are all above the value of the maxValue state.
     *
     * @returns {object[]}
     */
    const maxRange = useMemo(
        () =>
            options.filter(
                ({ value }) => Number(value) > Number(minValue.value)
            ),
        [options, minValue]
    );

    /**
     * Update the maxValue state.
     *
     * @param {Event}
     */
    const handleMaxRangeChange = useCallback(event => {
        setMaxValue({
            name: event.label,
            value: event.value
        });
    }, []);

    /**
     * Update the minValue state.
     *
     * @param {Event}
     */
    const handleMinRangeChange = useCallback(event => {
        setMinValue({
            name: event.label,
            value: event.value
        });
    }, []);

    /**
     * Get the correct number formatting.
     *
     * @param name
     * @returns {string}
     */
    const labelFormat = name => {
        if (title === 'PriceOnline') {
            return formatPriceValue(name);
        }

        if (title === 'Mileage') {
            return formatMileageValue(name);
        }

        return name;
    };

    /**
     * Update the global globalContext based on the changed min or max values.
     */
    useEffect(() => {
        if (didMount.current === true) {
            setGlobalContext(({ searchState, facets, ...prevContext }) => ({
                ...prevContext,
                facets: {
                    ...facets,
                    [title]: {
                        range: [minValue.value, maxValue.value]
                    }
                },
                start: 1,
                searchState: searchStates.UPDATING,
                prevSearchState: searchState
            }));
        } else {
            didMount.current = true;
        }
    }, [didMount, minValue, maxValue, title, setGlobalContext]);

    useEffect(() => {
        console.debug('minValue');
    }, [minValue]);

    useEffect(() => {
        console.debug('maxValue');
    }, [maxValue]);

    useEffect(() => {
        console.debug('title');
    }, [title]);

    return (
        <>
            <Select
                name={title}
                classNamePrefix="react-select"
                onChange={event => {
                    handleMinRangeChange(event);
                }}
                options={minRange.map(({ value, name }) => ({
                    value: value,
                    label: labelFormat(name)
                }))}
                value={{
                    value: minValueContextData ?? minStartValue.value,
                    label: minValueContextData
                        ? labelFormat(minValueContextData)
                        : labelFormat(minStartValue.name)
                }}
            />

            <Select
                name={title}
                onChange={event => {
                    handleMaxRangeChange(event);
                }}
                classNamePrefix="react-select"
                options={maxRange.map(({ value, name }) => ({
                    value: value,
                    label: labelFormat(name)
                }))}
                value={{
                    value: maxValueContextData ?? maxStartValue.value,
                    label: maxValueContextData
                        ? labelFormat(maxValueContextData)
                        : labelFormat(maxStartValue.name)
                }}
            />
        </>
    );
};

export default Range;
